<!-- src/views/CardStatusList.vue -->
<template>
  <div class="container-fluid py-4">
    <div class="d-flex">
      <!-- Navigation Button -->
      <button class="btn btn-primary" @click="$router.push('/create-workspace-agents')">
        Create New
      </button>
    </div>
  </div>

  <div class="container-fluid">
    <div class="row starter-main">
      <CommonTable v-if="crud.isLoading" :tableObj="tableConfig" :title="title" @edit="handleEdit"
        @delete="handleDelete" @view="handleView" />
      <div v-else class="text-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal id="createNew" :title="`Create ${pageTitle}`" :visible="isCreateModalOpen" @close="closeCreateModal"
    @save="handleSaveCreate" :save-button-disabled="isCreateFormInvalid">
    <template #body>
      <BaseForm :fields="createFormFields" v-model="newItemData" @submit="handleSaveCreate" ref="createFormRef" />
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal id="editModal" :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`" :visible="isEditModalOpen"
    @close="closeEditModal" @save="handleSaveEdit" :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormInvalid">
    <template #body>
      <BaseForm :fields="editFormFields" v-model="editingItemData" @submit="handleSaveEdit" ref="editFormRef" />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue";
// import zeroConfiguration from "@/components/commen/table/zeroConfiguration.vue";
import { useRouter } from "vue-router";
import CommonTable from "@/components/commen/table/CommonTable.vue";

const API_RESOURCE = "common/agents";
const pageTitle = "Workspace Agents";
const title = "Workspace Agents";

const crud = useCrud(API_RESOURCE);
const router = useRouter();

// --- Table Config ---
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Description", field: "description", sortable: true },
  ],
  data: crud.data.value,
  actions: [true],
  loading: true,
});

watch(crud.data, (newVal) => {
  tableConfig.value.data = newVal;
});
watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({ title: "", description: "" });
const editingItemData = ref({ _id: "", title: "", description: "" });

// --- Form Fields ---
const createFormFields = [
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter title",
  },
  {
    key: "description",
    label: "Description",
    type: "text",
    required: true,
    placeholder: "Enter description",
  },
];

const editFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter title",
    readonly: isViewMode.value,
  },
  {
    key: "description",
    label: "Description",
    type: "text",
    required: true,
    placeholder: "Enter description",
    readonly: isViewMode.value,
  },
]);

// --- Validation ---
const isCreateFormInvalid = computed(
  () => !newItemData.value.title.trim() || !newItemData.value.description.trim()
);
const isEditFormInvalid = computed(
  () =>
    !editingItemData.value.title?.trim() ||
    !editingItemData.value.description?.trim()
);

// --- Handlers ---
const handleEdit = (row: any) => {
  if (!row._id) return;
  editingItemData.value = { ...row };
  isViewMode.value = false;
  isEditModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this item?")) {
    await crud.deleteItem(id);
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormInvalid.value) return;
  const payload = { ...newItemData.value };
  const result = await crud.createItem(payload);
  if (result) closeCreateModal();
};

const handleSaveEdit = async () => {
  if (isEditFormInvalid.value) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(_id, updatePayload);
  if (result) closeEditModal();
};

// --- Close Modals ---
const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = { title: "", description: "" };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = { _id: "", title: "", description: "" };
};

// --- Lifecycle ---
onMounted(async () => {
  await crud.fetchData();
});
</script>

 